const STORAGE_KEY = 'fin_ai_repair_bundle_confidence_v1';

const safeWindow = () =>
  typeof window !== 'undefined' && window?.sessionStorage ? window : null;

const normalizeText = value => String(value || '').trim();

const readStore = () => {
  const browserWindow = safeWindow();
  if (!browserWindow) return {};

  try {
    const raw = browserWindow.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = store => {
  const browserWindow = safeWindow();
  if (!browserWindow) return;

  try {
    browserWindow.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore storage failures
  }
};

export const buildRepairBundleConfidenceKey = ({ assistantId = '', bundle = {} } = {}) => {
  const normalizedAssistantId = normalizeText(assistantId);
  const normalizedBundle = bundle && typeof bundle === 'object' ? bundle : {};
  return [
    normalizedAssistantId,
    normalizeText(normalizedBundle.recommendationCode),
    normalizeText(normalizedBundle.strategyGroupTitle),
    normalizeText(normalizedBundle.focusLabel),
    normalizeText(normalizedBundle.title),
  ].join('::');
};

export const loadRepairBundleRuns = ({ assistantId = '', bundle = {} } = {}) => {
  const key = buildRepairBundleConfidenceKey({ assistantId, bundle });
  if (!key || /^:+$/.test(key.replace(/[^:]/g, ''))) return [];

  const store = readStore();
  const runs = Array.isArray(store[key]) ? store[key] : [];
  return runs
    .map(run => ({
      ranAt: normalizeText(run?.ranAt),
      status: normalizeText(run?.status),
      tone: normalizeText(run?.tone),
      deliveryMode: normalizeText(run?.deliveryMode),
      sourceKind: normalizeText(run?.sourceKind),
      contentGapReason: normalizeText(run?.contentGapReason),
      scopeWarning: normalizeText(run?.scopeWarning),
    }))
    .filter(run => run.ranAt || run.status);
};

export const saveRepairBundleRun = ({
  assistantId = '',
  bundle = {},
  run = {},
  limit = 5,
} = {}) => {
  const key = buildRepairBundleConfidenceKey({ assistantId, bundle });
  if (!key || /^:+$/.test(key.replace(/[^:]/g, ''))) return [];

  const store = readStore();
  const currentRuns = Array.isArray(store[key]) ? store[key] : [];
  const nextRuns = [
    {
      ranAt: normalizeText(run?.ranAt),
      status: normalizeText(run?.status),
      tone: normalizeText(run?.tone),
      deliveryMode: normalizeText(run?.deliveryMode),
      sourceKind: normalizeText(run?.sourceKind),
      contentGapReason: normalizeText(run?.contentGapReason),
      scopeWarning: normalizeText(run?.scopeWarning),
    },
    ...currentRuns,
  ].slice(0, Number(limit) || 5);
  store[key] = nextRuns;
  writeStore(store);
  return nextRuns;
};

export const summarizeRepairBundleConfidence = runs => {
  const recentRuns = Array.isArray(runs) ? runs.slice(0, 3) : [];
  if (!recentRuns.length) {
    return {
      level: 'unproven',
      tone: 'info',
      title: 'Repair confidence not established yet',
      body:
        'You need at least one clean validation run before this repair can move toward release checks.',
      nextStep:
        'Run the seeded query after your latest edit and use the verdict below as the first proof point.',
    };
  }

  const latestRun = recentRuns[0] || {};
  const successCount = recentRuns.filter(run => normalizeText(run?.status) === 'success').length;
  const dangerCount = recentRuns.filter(run => normalizeText(run?.status) === 'danger').length;

  if (normalizeText(latestRun?.status) === 'danger' || dangerCount > 0) {
    return {
      level: 'blocked',
      tone: 'danger',
      title: 'Repair confidence is blocked',
      body:
        'At least one recent validation run still looks too close to the escalated pattern, so this repair should not move toward release yet.',
      nextStep:
        'Go back to the owner workspace, tighten the change again, and rerun the same seeded query.',
    };
  }

  if (successCount >= 2 && recentRuns.length >= 2) {
    return {
      level: 'ready',
      tone: 'success',
      title: 'Repair confidence is ready for release checks',
      body:
        'Multiple recent validation runs stayed cleaner than the escalated pattern, so this repair is ready for a broader Analyze and Deploy check.',
      nextStep:
        'Open Analyze to confirm the wider hotspot improved, then move into Deploy checklist review.',
    };
  }

  return {
    level: 'observe',
    tone: 'warning',
    title: 'Repair confidence is improving, but still needs observation',
    body:
      'You have at least one cleaner validation run, but not enough repeated proof to call the repair stable.',
    nextStep:
      'Keep the same seeded query, rerun after one more adjustment if needed, and wait for another clean result before release review.',
  };
};
